import { createReducer } from 'app/state/utils'
import * as types from './types'
import { ActivityItem, TradeNoteReadPatch, UnreadCounts, sortActivityItems } from './types'

export interface TradeNotesState {
  items: ActivityItem[]
  /** Wholesale-replaced on each load: the backend computes them, this slice only displays. */
  unreadCounts: UnreadCounts
}

const EMPTY_UNREAD_COUNTS: UnreadCounts = {
  byMasterTrade: {},
  bySubTrade: {},
}

const initialState: TradeNotesState = {
  items: [],
  unreadCounts: EMPTY_UNREAD_COUNTS,
}

// Upsert by item id rather than replace. Callers load different scopes — the master-trade tab
// loads a whole trade, a sub-trade view loads one sub-trade — so a wholesale replace would
// drop items outside the latest request. Notes are immutable (ADR_LOS-028: create-only) and
// never removed server-side, so within a scope a successful load is always a superset of the
// previous one: merge-on-success cannot hide a removal. FAILED loads never dispatch here at
// all (the thunks return {success:false} and the caller toasts) — an empty payload reaching
// this reducer therefore always means a genuinely empty successful response.
//
// A write's own response (a created note, a Mark, an Undo) is the server telling us the
// result, so it always replaces the stored copy.
const upsertItems = (existing: ActivityItem[], incoming: ActivityItem[]): ActivityItem[] => {
  const byId = new Map(existing.map(item => [item.id, item]))
  incoming.forEach(item => byId.set(item.id, item))
  return sortActivityItems(Array.from(byId.values()))
}

// Only a REFRESH can be stale (LOS-7094): one read from PQS just before PQS caught up with a
// Mark or an Undo would, if it always won, overwrite the fresher copy that write returned —
// "New" again after a Mark, "Actioned" again after an Undo. "Keep an existing readAt" cannot
// fix it, because an Undo legitimately removes the receipt. Instead each copy is judged by its
// last event: every real change moves max(readAt, reopenedAt) forward (Mark sets readAt, Undo
// a later reopenedAt, a re-Mark a later readAt), so a stale snapshot never has the later one.
// A refresh replaces the stored copy unless that copy saw a strictly later event; on a tie the
// refresh wins, so it still carries everything else (e.g. re-resolved names).

const ISO_UTC = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/

/**
 * An ISO UTC time with its fraction padded to six digits, so two of them compare as text.
 * The ledger's times are UTC (`…Z`) but vary in fractional digits, which plain text order
 * gets wrong ('.5Z' sorts before '.25Z'). '' when there is no time — and for anything that is
 * not an ISO UTC time, which would otherwise sort after every real one and block later
 * refreshes of the note.
 */
export const eventTime = (time?: string | null): string =>
  time && ISO_UTC.test(time)
    ? time.replace(/(\.\d*)?Z$/, (_, fraction = '.') => `${(fraction + '000000').slice(0, 7)}Z`)
    : ''

/** A note's last read-state event, max(readAt, reopenedAt), as comparable text. */
export const lastEventAt = (item: ActivityItem): string =>
  item.kind === 'note' ? [eventTime(item.readAt), eventTime(item.reopenedAt)].sort().pop() ?? '' : ''

const mergeRefresh = (existing: ActivityItem[], incoming: ActivityItem[]): ActivityItem[] => {
  const byId = new Map(existing.map(item => [item.id, item]))
  incoming.forEach(item => {
    const current = byId.get(item.id)
    if (!current || lastEventAt(current) <= lastEventAt(item)) byId.set(item.id, item)
  })
  return sortActivityItems(Array.from(byId.values()))
}

/**
 * Apply "Mark all" receipt patches (LOS-7093) to the notes already in the store, by note id —
 * the Signing Portal drawer's applyReadPatches, for this store. Only the read fields change, so
 * order and every other field are kept. A note without a patch (one the batch skipped, or not
 * in it) is returned untouched, so a partial failure leaves exactly those still unactioned; a
 * patch for a note not in the store is ignored.
 *
 * The same "latest event wins" rule as mergeItems: a patch older than the note's last event is
 * ignored. Otherwise a slow Mark all response could land after a refresh that already shows
 * another user's later Undo, and put the receipt back. An accepted patch also drops the reopen
 * fields, as the backend does for any note with a receipt (a receipt always post-dates the
 * reopens it follows), so a patched note reads exactly like one the next refresh will return.
 */
export const patchReadReceipts = (
  items: ActivityItem[],
  patches: TradeNoteReadPatch[],
): ActivityItem[] => {
  if (patches.length === 0) return items
  const byNoteId = new Map(patches.map(patch => [patch.noteId, patch]))
  return items.map(item => {
    const patch = item.kind === 'note' ? byNoteId.get(item.id) : undefined
    if (!patch || eventTime(patch.readAt) < lastEventAt(item)) return item
    return {
      ...item,
      readAt: patch.readAt,
      readByAuth0Id: patch.readByAuth0Id,
      readByName: patch.readByName,
      reopenedAt: null,
      reopenedByAuth0Id: null,
      reopenedByName: null,
    }
  })
}

const reducer = createReducer(initialState)({
  [types.LOAD_TRADE_NOTES]: (state: TradeNotesState, action: any) => ({
    ...state,
    items: mergeRefresh(state.items, action.payload ?? []),
  }),

  // A write's own response: a just-created note (placed so the author sees it without waiting
  // for a refetch), or the note a Mark or an Undo returned. Always replaces the stored copy.
  [types.ADD_TRADE_NOTE]: (state: TradeNotesState, action: any) => ({
    ...state,
    items: action.payload ? upsertItems(state.items, [action.payload]) : state.items,
  }),

  [types.APPLY_READ_PATCHES]: (state: TradeNotesState, action: any) => ({
    ...state,
    items: patchReadReceipts(state.items, action.payload ?? []),
  }),

  [types.LOAD_UNREAD_COUNTS]: (state: TradeNotesState, action: any) => ({
    ...state,
    // Spread over the empty shape so a partial response still yields usable maps.
    unreadCounts: action.payload
      ? { ...EMPTY_UNREAD_COUNTS, ...action.payload }
      : EMPTY_UNREAD_COUNTS,
  }),
})

export default reducer
